/* eslint-disable react/forbid-prop-types */
import React, { useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import { OverlayTrigger, Tooltip, Modal } from 'react-bootstrap';
import AppButton from '../../shared/components/app-button/AppButton';
import AppLink from '../../shared/components/app-link/AppLink';
import {
  formatCurrency,
  formatTimeString,
  isMobileWidth,
} from '../../shared/helper-utils/appUtils';
import AppFlightLogo from '../../shared/components/app-flight-logo/AppFlightLogo';
import FareSummary from '../flights/FareSummary';

function ResultsFooter({
  journeyType,
  selectedFare,
  selectedFlight,
  domint,
  searchRequest,
  onBook,
  selectedClassInfo,
  showInvoiceFare,
}) {
  const intl = useIntl();
  const formattedMessage = (message) => {
    return intl.formatMessage({ id: message });
  };

  const [openFareSummary, setOpenFareSummary] = useState(false);
  const fareSummaryRef = useRef(undefined);

  const getKValue = (list) => {
    let kValue = 0;
    if (list.length > 0) {
      list.forEach((tax) => {
        if (tax.taxcode.includes('ADT_K3')) {
          kValue = tax.taxamount;
        } else if (
          tax.taxcode.indexOf('ADT') > -1 &&
          tax.taxcode.indexOf('GST') > -1
        ) {
          kValue = tax.taxamount;
        }
      });
    }
    return kValue;
  };

  const handleShowFareSummary = () => {
    const isSingleLeg = journeyType === 'oneway' || domint === 'international';

    const adultBaseFare = isSingleLeg
      ? selectedFare.onward.adultbasefare
      : selectedFare.onward.adultbasefare + selectedFare.return.adultbasefare;
    const adultTotalTax = isSingleLeg
      ? selectedFare.onward.adulttax
      : selectedFare.onward.adulttax + selectedFare.return.adulttax;
    const adultFuelCharge = isSingleLeg
      ? selectedFare.onward.adultyq
      : selectedFare.onward.adultyq + selectedFare.return.adultyq;

    let adultKValue = 0;
    if (isSingleLeg) {
      const { othertaxesbreakup } = selectedFare.onward;
      const list = Array.isArray(othertaxesbreakup)
        ? othertaxesbreakup
        : [othertaxesbreakup];
      adultKValue = getKValue(list);
    } else {
      const { othertaxesbreakup: onwardTaxes } = selectedFare.onward;
      const onwardList = Array.isArray(onwardTaxes)
        ? onwardTaxes
        : [onwardTaxes];
      const { othertaxesbreakup: returnTaxes } = selectedFare.return;
      const returnList = Array.isArray(returnTaxes)
        ? returnTaxes
        : [returnTaxes];
      adultKValue = getKValue(onwardList) + getKValue(returnList);
    }

    const childBaseFare = isSingleLeg
      ? selectedFare.onward.childbasefare
      : selectedFare.onward.childbasefare + selectedFare.return.childbasefare;
    const childTotalTax = isSingleLeg
      ? selectedFare.onward.childtax
      : selectedFare.onward.childtax + selectedFare.return.childtax;
    const childFuelCharge = isSingleLeg
      ? selectedFare.onward.childyq
      : selectedFare.onward.childyq + selectedFare.return.childyq;

    const infantBaseFare = isSingleLeg
      ? selectedFare.onward.infantbasefare
      : selectedFare.onward.infantbasefare + selectedFare.return.infantbasefare;
    const infantTotalTax = isSingleLeg
      ? selectedFare.onward.infanttax
      : selectedFare.onward.infanttax + selectedFare.return.infanttax;
    const infantFuelCharge = isSingleLeg
      ? selectedFare.onward.infantyq
      : selectedFare.onward.infantyq + selectedFare.return.infantyq;

    const handlingCharges = isSingleLeg
      ? selectedFare.onward.handlingcharges
      : selectedFare.onward.handlingcharges +
        selectedFare.return.handlingcharges;
    const gst = isSingleLeg
      ? selectedFare.onward.servicetax
      : selectedFare.onward.servicetax + selectedFare.return.servicetax;
    const markUp = isSingleLeg
      ? selectedFare.onward.markup
      : selectedFare.onward.markup + selectedFare.return.markup;

    const sumFare = (onwardValue, returnValue) =>
      isSingleLeg ? onwardValue : onwardValue + returnValue;

    let discount = 0;
    let tds = 0;
    let totalNet = 0;
    if (showInvoiceFare) {
      discount = sumFare(
        selectedFare.onward.discount,
        selectedFare.return.discount,
      );
      tds = sumFare(selectedFare.onward.tds, selectedFare.return.tds);
      totalNet = sumFare(
        selectedFare.onward.totalnet,
        selectedFare.return.totalnet,
      );
    }

    const plb = isSingleLeg
      ? selectedFare.onward.plb
      : selectedFare.onward.plb + selectedFare.return.plb;

    const totalFq = isSingleLeg
      ? selectedFare.onward.totalfq
      : selectedFare.onward.totalfq + selectedFare.return.totalfq;

    fareSummaryRef.current = {
      adultBaseFare,
      childBaseFare,
      infantBaseFare,
      adultKValue,
      adultTotalTax,
      childTotalTax,
      infantTotalTax,
      adultFuelCharge,
      childFuelCharge,
      infantFuelCharge,
      handlingCharges,
      gst,
      tds,
      markUp,
      discount: discount + plb,
      seatPrice: 0,
      mealPrice: 0,
      baggagePrice: 0,
      totalNet,
      totalFare: totalFq,
    };
    setOpenFareSummary(true);
  };

  const getSelectedFlightDetail = (flight, fare, isOnward) => {
    let fareToShow = fare;
    const selectedClasses = [];
    if (
      isOnward &&
      selectedClassInfo &&
      selectedClassInfo.selectedOnwardClassFare
    ) {
      fareToShow = selectedClassInfo.selectedOnwardClassFare;
      selectedClassInfo.selectedOnwardList.forEach((x) => {
        selectedClasses.push(x.selectedclass);
      });
    } else if (
      selectedClassInfo &&
      selectedClassInfo.selectedReturnClassFare &&
      !isOnward
    ) {
      fareToShow = selectedClassInfo.selectedReturnClassFare;
      selectedClassInfo.selectedReturnList.forEach((x) => {
        selectedClasses.push(x.selectedclass);
      });
    }
    return (
      <div>
        <div className="fs-14 white-color mb-1">
          {isOnward
            ? `${searchRequest.origin} - ${searchRequest.destination}`
            : `${searchRequest.destination} - ${searchRequest.origin}`}
        </div>
        <div className="results-footer__flight-info">
          <div className="fs-12 grey-color d-flex justify-content-between align-items-center">
            <span>
              {isOnward ? 'Depart' : 'Return'} -{' '}
              {flight.flightlegs[0].validatingcarriername}
            </span>
            {selectedClasses.length > 0 && (
              <span className="fs-10">Class: {selectedClasses.join(',')}</span>
            )}
          </div>
          <div className="results-footer__flight-info_details">
            <div>
              <AppFlightLogo
                width="20px"
                height="20px"
                carrier={flight.flightlegs[0].carrier.substring(0, 2)}
              />
            </div>
            <div>{formatTimeString(flight.flightlegs[0].deptime)}</div>
            <div className="results-footer__seperator secondary-background-color" />
            <div>
              {formatTimeString(
                flight.flightlegs[flight.flightlegs.length - 1].arrtime,
              )}
            </div>
            <div className="secondary-color fs-16">
              {formatCurrency(fareToShow.totalfq)}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderFareBreakupTooltip = () => (
    <Tooltip id="fare-breakup-tooltip">
      <div className="fare-breakup">
        <div className="fare-breakup_content">
          <div className="fare-breakup_title">Base Fare</div>
          <div className="fare-breakup_value">
            {journeyType === 'oneway' || domint === 'international'
              ? formatCurrency(selectedFare.onward.totalbasefare)
              : formatCurrency(
                  selectedFare.onward.totalbasefare +
                    selectedFare.return.totalbasefare,
                )}
          </div>
        </div>
        <div className="fare-breakup_content">
          <div className="fare-breakup_title">Tax</div>
          <div className="fare-breakup_value">
            {journeyType === 'oneway' || domint === 'international'
              ? formatCurrency(selectedFare.onward.totaltax)
              : formatCurrency(
                  selectedFare.onward.totaltax + selectedFare.return.totaltax,
                )}
          </div>
        </div>
        <div className="fare-breakup_content">
          <div className="fare-breakup_title">HC</div>
          <div className="fare-breakup_value">
            {journeyType === 'oneway' || domint === 'international'
              ? formatCurrency(selectedFare.onward.handlingcharges)
              : formatCurrency(
                  selectedFare.onward.handlingcharges +
                    selectedFare.return.handlingcharges,
                )}
          </div>
        </div>
        <div className="fare-breakup_content">
          <div className="fare-breakup_title">GST</div>
          <div className="fare-breakup_value">
            {journeyType === 'oneway' || domint === 'international'
              ? formatCurrency(selectedFare.onward.servicetax)
              : formatCurrency(
                  selectedFare.onward.servicetax +
                    selectedFare.return.servicetax,
                )}
          </div>
        </div>
        <div className="fare-breakup_content">
          <div className="fare-breakup_title">Other Charges</div>
          <div className="fare-breakup_value">
            {journeyType === 'oneway' || domint === 'international'
              ? formatCurrency(selectedFare.onward.transactionfee)
              : formatCurrency(
                  selectedFare.onward.transactionfee +
                    selectedFare.return.transactionfee,
                )}
          </div>
        </div>

        {showInvoiceFare && (
          <>
            <div className="fare-breakup_content">
              <div className="fare-breakup_title">Discount</div>
              <div className="fare-breakup_value">
                {journeyType === 'oneway' || domint === 'international'
                  ? formatCurrency(selectedFare.onward.discount)
                  : formatCurrency(
                      selectedFare.onward.discount +
                        selectedFare.return.discount,
                    )}
              </div>
            </div>
            <div className="fare-breakup_content">
              <div className="fare-breakup_title">TDS</div>
              <div className="fare-breakup_value">
                {journeyType === 'oneway' || domint === 'international'
                  ? formatCurrency(selectedFare.onward.tds)
                  : formatCurrency(
                      selectedFare.onward.tds + selectedFare.return.tds,
                    )}
              </div>
            </div>
            <div className="fare-breakup_content">
              <div className="fare-breakup_title">Net Fare</div>
              <div className="fare-breakup_value">
                {journeyType === 'oneway' || domint === 'international'
                  ? formatCurrency(selectedFare.onward.totalnet)
                  : formatCurrency(
                      selectedFare.onward.totalnet +
                        selectedFare.return.totalnet,
                    )}
              </div>
            </div>
          </>
        )}
        <div className="fare-breakup_content">
          <div className="fare-breakup_title">Total Fare</div>
          <div className="fare-breakup_value">
            {journeyType === 'oneway' || domint === 'international'
              ? formatCurrency(selectedFare.onward.totalfq)
              : formatCurrency(
                  selectedFare.onward.totalfq + selectedFare.return.totalfq,
                )}
          </div>
        </div>
      </div>
    </Tooltip>
  );

  return (
    <footer className="results-footer">
      <section className="results-footer__container d-flex justify-content-between align-items-center">
        <div className="d-flex flex-sm-row flex-column">
          <div className="d-flex gap-2 align-items-center flex-sm-row flex-column">
            {selectedFlight.onward &&
              getSelectedFlightDetail(
                selectedFlight.onward,
                selectedFare.onward,
                true,
              )}
            {selectedFlight.return &&
              getSelectedFlightDetail(
                selectedFlight.return,
                selectedFare.return,
              )}
          </div>
        </div>
        {selectedFare.onward &&
          selectedFare.return &&
          (!isMobileWidth() ? (
            <div className="d-flex justify-content-end align-items-center">
              <OverlayTrigger
                placement="top"
                overlay={renderFareBreakupTooltip()}
              >
                <div className="me-5 text-center">
                  <p className="fs-12 mb-0 white-color">Total Fare</p>
                  <h4 className="results-footer__price mb-0 totalPrice">
                    {journeyType === 'oneway' || domint === 'international'
                      ? formatCurrency(selectedFare.onward.totalfq)
                      : formatCurrency(
                          selectedFare.onward.totalfq +
                            selectedFare.return.totalfq,
                        )}
                  </h4>
                  {showInvoiceFare && (
                    <p className="fs-12 mb-0 white-color">
                      {journeyType === 'oneway' || domint === 'international'
                        ? formatCurrency(selectedFare.onward.totalnet)
                        : formatCurrency(
                            selectedFare.onward.totalnet +
                              selectedFare.return.totalnet,
                          )}
                    </p>
                  )}
                  <AppLink
                    variant="white"
                    linkText={formattedMessage('summary_link')}
                    onClick={handleShowFareSummary}
                  />
                </div>
              </OverlayTrigger>

              <AppButton
                variant="primary"
                className="results-footer__book-btn"
                btnText={formattedMessage('continue_btn')}
                onClick={onBook}
              />
            </div>
          ) : (
            <div className="d-flex flex-column gap-3 justify-content-end align-items-center">
              <OverlayTrigger
                placement="top"
                overlay={renderFareBreakupTooltip()}
              >
                <div className="text-center">
                  <p className="fs-12 mb-0 white-color">Total Fare</p>
                  <h4 className="results-footer__price mb-0">
                    {journeyType === 'oneway' || domint === 'international'
                      ? formatCurrency(selectedFare.onward.totalfq)
                      : formatCurrency(
                          selectedFare.onward.totalfq +
                            selectedFare.return.totalfq,
                        )}
                  </h4>
                  {showInvoiceFare && (
                    <p className="fs-12 mb-0 white-color">
                      {journeyType === 'oneway' || domint === 'international'
                        ? formatCurrency(selectedFare.onward.totalnet)
                        : formatCurrency(
                            selectedFare.onward.totalnet +
                              selectedFare.return.totalnet,
                          )}
                    </p>
                  )}
                  <AppLink
                    variant="white"
                    linkText={formattedMessage('summary_link')}
                    onClick={handleShowFareSummary}
                  />
                </div>
              </OverlayTrigger>

              <AppButton
                variant="primary"
                className="results-footer__book-btn"
                btnText={formattedMessage('continue_btn')}
                onClick={onBook}
              />
            </div>
          ))}
      </section>

      {openFareSummary && (
        <Modal
          onHide={() => setOpenFareSummary(false)}
          backdrop="static"
          show={openFareSummary}
          keyboard={false}
        >
          <Modal.Header closeButton>
            <Modal.Title>{formattedMessage('fare_summary')}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <FareSummary
              fareSummary={fareSummaryRef.current}
              selectedFare={selectedFare}
              journeyType={journeyType}
              domint={domint}
              passengers={{
                numAdults: parseInt(searchRequest.numadults, 10),
                numChilds: parseInt(searchRequest.numchildren, 10),
                numInfants: parseInt(searchRequest.numinfants, 10),
              }}
            />
          </Modal.Body>
        </Modal>
      )}
    </footer>
  );
}

ResultsFooter.defaultProps = {
  domint: 'domestic',
};

ResultsFooter.propTypes = {
  journeyType: PropTypes.string.isRequired,
  selectedFare: PropTypes.any.isRequired,
  searchRequest: PropTypes.any.isRequired,
  selectedFlight: PropTypes.any.isRequired,
  onBook: PropTypes.func.isRequired,
  domint: PropTypes.string,
  selectedClassInfo: PropTypes.any.isRequired,
  showInvoiceFare: PropTypes.any.isRequired,
};

export default ResultsFooter;
